import {
   DatePicker,
   TimePicker,
   Select,
   Input,
   Space,
   Button as AntdButton,
} from 'antd'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import {
   useGetArrivalDataMutation,
   useGetAccommodationDataQuery,
   useGetCitiesDataMutation,
   useGetTrafficNameAllQuery,
   useGetUserQuery,
   useGetVehicleQuery,
   useRetrieveFilghtDataQuery,
   usePostTrafficDataMutation,
} from 'apis/services/arrival'
import HandleErrors from "utils/handleErrors";
import Button from 'components/general/Button'
import Icon from 'components/general/Icon'
import showToast from 'utils/showToast'

import styles from './styles.module.scss'

const aggregateData = (data: any) => {
   const sharedData = {
      type: data.type,
      adult: data.adult,
      child: data.child,
      note: data.note,
      name: data.name,
      pick_up: data.pick_up,
      guide: data.guide,
      leader: data.leader,
      transportation: data.transportation,
      arrival: data.arrival,
   }
   const tripData = {
      ...sharedData,
      trip_type: data.trip_type,
      date: data.date,
      time: data.time,
      city: data.city,
      drop_off: data.drop_off,
   }
   const flightData = {
      ...sharedData,
      flight: data.flight,
      visa: data.visa,
   }

   const chosen = data.type === 'trip' ? tripData : flightData

   let errorMessage = null

   if (!sharedData.arrival) {
      errorMessage = 'Please select the file number'
      return [errorMessage, null]
   }
   if (!sharedData.type) {
      errorMessage = 'Please select the type'
      return [errorMessage, null]
   }
   if (!sharedData.name) {
      errorMessage = 'Please select the pathway or flight path'
      return [errorMessage, null]
   }
   if (!sharedData.guide) {
      errorMessage = 'Please select the guide'
      return [errorMessage, null]
   }
   if (!sharedData.leader) {
      errorMessage = 'Please select the leader'
      return [errorMessage, null]
   }
   if (!sharedData.transportation) {
      errorMessage = 'Please select the driver'
      return [errorMessage, null]
   }
   if (!sharedData.pick_up) {
      errorMessage = 'Please select the Pick Up from or To/From'
      return [errorMessage, null]
   }
   if (!sharedData.adult || sharedData.adult < 0) {
      errorMessage = 'Please enter the number of adults'
      return [errorMessage, null]
   }
   if (!sharedData.child || sharedData.child < 0) {
      errorMessage = 'Please enter the number of kids'
      return [errorMessage, null]
   }
   if (data.type === 'trip') {
      if (!tripData.date) {
         errorMessage = 'Please select the date'
         return [errorMessage, null]
      }
      if (!tripData.time) {
         errorMessage = 'Please select the time'
         return [errorMessage, null]
      }
      if (!tripData.city) {
         errorMessage = 'Please select the city'
         return [errorMessage, null]
      }
      if (!tripData.drop_off) {
         errorMessage = 'Please enter the drop off to'
         return [errorMessage, null]
      }
   } else {
      if (!flightData.visa) {
         errorMessage = 'Please enter the visa'
         return [errorMessage, null]
      }
      if (!flightData.flight) {
         errorMessage = 'Please select the flight number'
         return [errorMessage, null]
      }
   }

   return [errorMessage, chosen]
}

export default function AddTrafficModel({
   onCancel,
   onConfirm,
}: {
   onCancel?: () => void
   onConfirm?: (data: any) => void
}) {
   const [selectedFileNumber, setSelectedFileNumber] = useState<any>(null)
   const [appendFiles, setAppendFiles] = useState<any>([])
   const [selectType, setSelectType] = useState<any>(null)
   const [tripType, setTripType] = useState<any>(null)
   const [pathway, setPathway] = useState<any>(null)
   const [pathwayLabel, setPathwayLabel] = useState<any>(null)
   const [date, setDate] = useState<any>(null)
   const [time, setTime] = useState<any>(null)
   const [city, setCity] = useState<any>(null)
   const [adults, setAdults] = useState<any>(null)
   const [kids, setKids] = useState<any>(null)
   const [pickUp, setPickUp] = useState<any>(null)
   const [dropOff, setDropOff] = useState<any>(null)
   const [leader, setLeader] = useState<any>(null)
   const [guide, setGuide] = useState<any>(null)
   const [driver, setDriver] = useState<any>(null)
   const [flight, setFlight] = useState<any>(null)
   const [note, setNote] = useState<any>(null)
   const [visa, setVisa] = useState<any>(null)

   const [getArrivalsData] = useGetArrivalDataMutation()
   const [getCitiesData] = useGetCitiesDataMutation()
   const { data: pathwaysFetchedData, isLoading: isPathwaysDataLoading } =
      useGetTrafficNameAllQuery({})
   const {
      data: accommodationFetchedData,
      isLoading: isAccommodationDataLoading,
      refetch: refetchAccommodationData,
   } = useGetAccommodationDataQuery(
      {
         page_size: 1000,
         arrival: selectedFileNumber,
      },
      { skip: !selectedFileNumber }
   )
   const { data: leaderFetchedData, isLoading: isLeaderDataLoading } =
      useGetUserQuery({
         page_size: 1000,
         role__name: 'Leader',
      })
   const { data: guideFetchedData, isLoading: isGuideDataLoading } =
      useGetUserQuery({
         page_size: 1000,
         role__name: 'Guide',
      })
   const {
      data: driverFetchedData,
      isLoading: isDriverLoading,
      refetch: refetchDriverData,
   } = useGetVehicleQuery({
      page_size: 1000,
      search: pathwayLabel,
   })
   const {
      data: flightFetchedData,
      isLoading: isLoadingFlightData,
      refetch: refetchFlightData,
   } = useRetrieveFilghtDataQuery(
      {
         page_size: 1000,
         arrival: selectedFileNumber,
      },
      { skip: !selectedFileNumber }
   )
   const [postPathway] = usePostTrafficDataMutation()

   const [arrivalData, setArrivalData] = useState<any>([])
   const [pathwaysData, setPathwaysData] = useState<any>([])
   const [citiesData, setCitiesData] = useState<any>([])
   const [accommodationData, setAccommodationData] = useState<any>([])
   const [leadersData, setLeadersData] = useState<any>([])
   const [guidesData, setGuidesData] = useState<any>([])
   const [driversData, setDriversData] = useState<any>([])
   const [flightsData, setFlightsData] = useState<any>([])

   useEffect(() => {
      getArrivalsData({
         page_size: 1000,
      })
         .unwrap()
         .then((value) => {
            if (value?.results) {
               const data: any[] = []
               value?.results.forEach((arrival: any) => {
                  data.push({
                     id: arrival.id,
                     agentFileNum: arrival.agent_file_number,
                     fileNum: arrival.file_number,
                  })
               })

               setArrivalData(data)
            }
         })

         .catch((err) => {
            console.log('error', err)
         })

      getCitiesData()
         .unwrap()
         .then((value) => {
            if (value?.results) {
               setCitiesData(value?.results)
            }
         })
         .catch((err) => {
            console.log('error', err)
         })
   }, [])

   useEffect(() => {
      if (isPathwaysDataLoading) return
      setPathwaysData(pathwaysFetchedData)
   }, [isPathwaysDataLoading])
   useEffect(() => {
      if (isAccommodationDataLoading) return
      setAccommodationData(accommodationFetchedData?.results ?? [])
   }, [isAccommodationDataLoading])
   useEffect(() => {
      if (isLeaderDataLoading) return
      setLeadersData(leaderFetchedData?.results ?? [])
   }, [isLeaderDataLoading])
   useEffect(() => {
      if (isGuideDataLoading) return
      setGuidesData(guideFetchedData?.results ?? [])
   }, [isGuideDataLoading])
   useEffect(() => {
      if (isDriverLoading) return
      setDriversData(driverFetchedData?.results ?? [])
   }, [isDriverLoading])
   useEffect(() => {
      if (isLoadingFlightData) return
      setFlightsData(flightFetchedData?.results ?? [])
   }, [isLoadingFlightData])

   useEffect(() => {
      if (!selectedFileNumber || isAccommodationDataLoading) return
      setPickUp(null)
      refetchAccommodationData()
         .unwrap()
         .then((value) => {
            if (value?.results) {
               setAccommodationData((prev: any) => {
                  if (!prev) return value?.results
                  if (!value?.results) return prev
                  const newData = [...prev, ...value.results]
                  const uniqueData = newData.filter(
                     (item: any, index: number, self: any) =>
                        index ===
                        self.findIndex(
                           (t: any) => t.id === item.id
                        )
                  )
                  return uniqueData
               })
            }
         })
         .catch((err) => {
            console.log('error', err)
         })
   }, [selectedFileNumber])
   useEffect(() => {
      if (!pathway || isDriverLoading) return
      setDriver(null)

      refetchDriverData()
         .unwrap()
         .then((value) => {
            if (value?.results) {
               setDriversData((prev: any) => {
                  if (!prev) return value?.results
                  if (!value?.results) return prev
                  const newData = [...prev, ...value.results]
                  const uniqueData = newData.filter(
                     (item: any, index: number, self: any) =>
                        index ===
                        self.findIndex(
                           (t: any) => t.id === item.id
                        )
                  )
                  return uniqueData
               })
            }
         })
         .catch((err) => {
            console.log('error', err)
         })
   }, [pathway])
   useEffect(() => {
      if (!selectedFileNumber || isLoadingFlightData) return
      setFlight(null)

      refetchFlightData()
         .unwrap()
         .then((value) => {
            if (value?.results) {
               setFlightsData((prev: any) => {
                  if (!prev) return value?.results
                  if (!value?.results) return prev
                  const newData = [...prev, ...value.results]
                  console.log('newData', newData, '')
                  const uniqueData = newData.filter(
                     (item: any, index: number, self: any) =>
                        index ===
                        self.findIndex(
                           (t: any) => t.id === item.id
                        )
                  )
                  return uniqueData
               })
            }
         })
         .catch((err) => {
            console.log('error', err)
         })
   }, [selectedFileNumber])

   const handelConfirm = async () => {
      const data = {
         type: selectType,
         trip_type: tripType,
         date,
         adult: adults,
         child: kids,
         drop_off: dropOff,
         time,
         // transportation_fees: 0, // driversData.find((driver: any) => driver.id === driver)?.vehicle_fees,
         visa,
         note,
         name: pathway,
         flight,
         pick_up: pickUp,
         guide,
         leader,
         transportation: driver,
         city,
         arrival: selectedFileNumber,
      }

      const [errorMessage, chosenData] = aggregateData(data)
      if (errorMessage) {
         showToast({
            toastType: 'danger',
            msg: errorMessage as string,
         })
         return
      }

      if (!chosenData || typeof chosenData === 'string') return

      const promises: Promise<any>[] = []
      appendFiles.forEach((file: any) => {
         const fileData = arrivalData.find(
            (arrival: any) => arrival.id === file
         )
         if (!fileData) return

         promises.push(
            postPathway({
               ...chosenData,
               arrival: file,
            })
               .unwrap()
               .then((value) => {
                  showToast({
                     toastType: 'success',
                     msg: `File ${fileData.agentFileNum} - ${fileData.fileNum} added successfully`,
                  })
               })
               .catch((err) => {
                  HandleErrors(err)
               })
         )
      })

      await Promise.all(promises)
      onConfirm?.(null)
   }

   return (
      <div className={styles.container}>
         <div className={styles.selectDriver}>
            <div className={styles.title}>Select the file number</div>
            <Select
               showSearch
               className={styles.select}
               placeholder="Select file number"
               style={{ marginTop: '10px', width: '100%' }}
               options={arrivalData.map((arrival: any) => ({
                  value: arrival.id,
                  label: `${arrival.agentFileNum} - ${arrival.fileNum}`,
               }))}
               value={selectedFileNumber}
               onChange={(value) => {
                  setSelectedFileNumber(value)
                  const newFiles = appendFiles.filter(
                     (file: any) => file !== value
                  )
                  newFiles.push(value)
                  setAppendFiles(newFiles)
               }}
               filterOption={(input, option: any) =>
                  (option?.label?.toLowerCase() ?? '').includes(
                     input.toLowerCase()
                  )
               }
            />
         </div>

         <Space wrap style={{ marginBottom: '10px' }}>
            {appendFiles.map((file: any) => {
               const fileData = arrivalData.find(
                  (arrival: any) => arrival.id === file
               )
               if (!fileData) return null

               return (
                  <div
                     key={file}
                     style={{ display: 'flex', alignItems: 'center' }}
                  >
                     <AntdButton
                        type="primary"
                        shape="round"
                        size="small"
                        onClick={() => {
                           setSelectedFileNumber(file)
                        }}
                     >
                        {fileData.agentFileNum} - {fileData.fileNum}
                     </AntdButton>

                     {/* X Button */}
                     <AntdButton
                        type="dashed"
                        shape="round"
                        size="small"
                        onClick={() => {
                           const newFiles = appendFiles.filter(
                              (f: any) => f !== file
                           )
                           setAppendFiles(newFiles)
                           setFlightsData((prev: any) => {
                              if (!prev) return []
                              return prev.filter((f: any) => f.arrival !== file)
                           })
                           if (newFiles.length === 0) {
                              setSelectedFileNumber(null)
                           } else {
                              setSelectedFileNumber(newFiles[0])
                           }
                        }}
                     >
                        <Icon name="close" size={12} />
                     </AntdButton>
                  </div>
               )
            })}
         </Space>

         <div style={{ display: selectedFileNumber ? 'block' : 'none' }}>
            <div className={styles.title}>Select the type</div>
            <div
               style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '10px',
                  width: '100%',
               }}
            >
               <Button
                  onClick={() => setSelectType('trip')}
                  isOutline={selectType !== 'trip'}
                  btnClassName="mr-2"
               >
                  Trip
               </Button>
               <Button
                  onClick={() => setSelectType('flight')}
                  isOutline={selectType !== 'flight'}
               >
                  Flight
               </Button>
            </div>
         </div>

         <div
            style={{
               display: selectType ? 'block' : 'none',
               width: '100%',
            }}
         >
            <div
               style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: '10px',
                  width: '100%',
               }}
            >
               <div
                  style={{
                     display: selectType === 'trip' ? 'block' : 'none',
                     marginRight: '10px',
                     width: '100%',
                  }}
               >
                  <div className={styles.title}>Select Trip Type</div>
                  <Select
                     showSearch
                     className={styles.select}
                     placeholder="Select the trip type"
                     options={[
                        { value: 'optional', label: 'Optional' },
                        { value: 'ausflugspaket', label: 'Ausflugspaket' },
                     ]}
                     onChange={(value) => {
                        setTripType(value)
                     }}
                     style={{
                        marginTop: '10px',
                        width: '100%',
                     }}
                     value={tripType}
                     filterOption={(input, option) =>
                        (option?.label?.toLowerCase() ?? '').includes(
                           input.toLowerCase()
                        )
                     }
                  />
               </div>
               <div style={{ width: '100%' }}>
                  <div className={styles.title}>
                     Select {selectType === 'trip' ? 'Pathway' : 'Flight'}
                  </div>
                  <Select
                     showSearch
                     className={styles.select}
                     placeholder="Select the pathway"
                     options={
                        pathwaysData?.map((pathway: any) => ({
                           value: pathway.id,
                           label: pathway.name,
                        })) ?? []
                     }
                     onChange={(value) => {
                        setPathway(value)
                        const selectedPathway = pathwaysData.find(
                           (pathway: any) => pathway.id === value
                        )
                        setPathwayLabel(selectedPathway?.name)
                     }}
                     style={{
                        marginTop: '10px',
                        width: '100%',
                     }}
                     value={pathway}
                     filterOption={(input, option: any) =>
                        (option?.label?.toLowerCase() ?? '').includes(
                           input.toLowerCase()
                        )
                     }
                  />
               </div>
            </div>

            <div
               style={{
                  display: selectType === 'trip' ? 'flex' : 'none',
                  marginTop: '10px',
                  width: '100%',
               }}
            >
               <div style={{ marginRight: '10px', width: '100%' }}>
                  <div className={styles.title}>Select Date</div>
                  <DatePicker
                     className={styles.datePicker}
                     style={{ marginTop: '10px', width: '100%' }}
                     onChange={(date, dateString) => {
                        setDate(dateString)
                     }}
                     value={date ? dayjs(date) : null}
                  />
               </div>

               <div style={{ width: '100%' }}>
                  <div className={styles.title}>Select Time</div>
                  <TimePicker
                     format="HH:mm"
                     className={styles.datePicker}
                     style={{ marginTop: '10px', width: '100%' }}
                     onChange={(time, timeString) => {
                        setTime(timeString)
                     }}
                     value={time ? dayjs(time, 'HH:mm') : null}
                  />
               </div>
            </div>

            <div
               style={{
                  marginTop: '10px',
                  width: '100%',
                  display: selectType === 'trip' ? 'block' : 'none',
               }}
            >
               <div className={styles.title}>Select city</div>
               <Select
                  showSearch
                  className={styles.select}
                  placeholder="Select the city"
                  options={
                     citiesData?.map((city: any) => ({
                        value: city.id,
                        label: city.name,
                     })) ?? []
                  }
                  onChange={(value) => {
                     setCity(value)
                  }}
                  style={{
                     marginTop: '10px',
                     width: '100%',
                  }}
                  value={city}
                  filterOption={(input, option: any) =>
                     (option?.label?.toLowerCase() ?? '').includes(
                        input.toLowerCase()
                     )
                  }
               />
            </div>

            <div
               style={{
                  marginTop: '10px',
                  width: '100%',
                  display: selectType === 'flight' ? 'block' : 'none',
               }}
            >
               <div className={styles.title}>Select Flight Num.</div>
               <Select
                  showSearch
                  className={styles.select}
                  placeholder="Select the flight number"
                  options={
                     flightsData?.map((flight: any) =>{
                        const file = arrivalData.find(
                           (arrival: any) => arrival.id === flight.arrival
                        )

                        return {
                           value: flight.id,
                           label: `${flight.flight_number}`,
                        }
                     }) ?? []
                  }
                  onChange={(value) => {
                     setFlight(value)
                  }}
                  value={flight}
                  style={{
                     marginTop: '10px',
                     width: '100%',
                  }}
                  filterOption={(input, option: any) =>
                     (option?.label?.toLowerCase() ?? '').includes(
                        input.toLowerCase()
                     )
                  }
               />
            </div>

            <div
               style={{
                  display: 'flex',
                  marginTop: '10px',
                  flexDirection: 'row',
                  width: '100%',
               }}
            >
               <div style={{ marginRight: '10px', width: '100%' }}>
                  <div className={styles.title}>No. of Adults</div>
                  <Input
                     className={styles.input}
                     placeholder="Enter number of adults"
                     type="number"
                     style={{ marginTop: '10px', width: '100%' }}
                     onChange={(e) => {
                        setAdults(e.target.value)
                     }}
                     value={adults}
                  />
               </div>
               <div style={{ width: '100%' }}>
                  <div className={styles.title}>No. of Kids</div>
                  <Input
                     className={styles.input}
                     placeholder="Enter number of kids"
                     type="number"
                     style={{ marginTop: '10px', width: '100%' }}
                     onChange={(e) => {
                        setKids(e.target.value)
                     }}
                     value={kids}
                  />
               </div>
            </div>

            <div
               style={{
                  display: selectType === 'trip' ? 'flex' : 'none',
                  marginTop: '10px',
                  flexDirection: 'row',
                  width: '100%',
               }}
            >
               <div style={{ marginRight: '10px', width: '100%' }}>
                  <div className={styles.title}>Pick up From</div>
                  <Select
                     showSearch
                     className={styles.select}
                     placeholder="Select the pick up from"
                     options={
                        accommodationData?.map((accommodation: any) => ({
                           value: accommodation.id,
                           label: accommodation?.hotel_cruise?.name,
                        })) ?? []
                     }
                     onChange={(value) => {
                        setPickUp(value)
                     }}
                     style={{
                        marginTop: '10px',
                        width: '100%',
                     }}
                     value={pickUp}
                     filterOption={(input, option: any) =>
                        (option?.label?.toLowerCase() ?? '').includes(
                           input.toLowerCase()
                        )
                     }
                  />
               </div>
               <div style={{ display: 'block', width: '100%' }}>
                  <div className={styles.title}>Drop off To</div>
                  <Input
                     className={styles.input}
                     placeholder="Enter drop off to"
                     type="text"
                     style={{ marginTop: '10px', width: '100%' }}
                     onChange={(e) => {
                        setDropOff(e.target.value)
                     }}
                     value={dropOff}
                  />
               </div>
            </div>

            <div
               style={{
                  marginTop: '10px',
                  width: '100%',
                  display: selectType === 'flight' ? 'block' : 'none',
               }}
            >
               <div style={{ marginRight: '10px', width: '100%' }}>
                  <div className={styles.title}>To/From</div>
                  <Select
                     showSearch
                     className={styles.select}
                     placeholder="Select the pick up from"
                     options={
                        accommodationData?.map((accommodation: any) => ({
                           value: accommodation.id,
                           label: accommodation?.hotel_cruise?.name,
                        })) ?? []
                     }
                     onChange={(value) => {
                        setPickUp(value)
                     }}
                     style={{
                        marginTop: '10px',
                        width: '100%',
                     }}
                     value={pickUp}
                     filterOption={(input, option: any) =>
                        (option?.label?.toLowerCase() ?? '').includes(
                           input.toLowerCase()
                        )
                     }
                  />
               </div>
            </div>

            <div
               style={{
                  display: 'flex',
                  marginTop: '10px',
                  flexDirection: 'row',
                  width: '100%',
               }}
            >
               <div style={{ marginRight: '10px', width: '100%' }}>
                  <div className={styles.title}>Select Guide</div>
                  <Select
                     showSearch
                     className={styles.select}
                     placeholder="Select the guide"
                     options={
                        guidesData?.map((guide: any) => ({
                           value: guide.id,
                           label: guide.username,
                        })) ?? []
                     }
                     onChange={(value) => {
                        console.log('value', value)
                        setGuide(value)
                     }}
                     value={guide}
                     style={{
                        marginTop: '10px',
                        width: '100%',
                     }}
                     filterOption={(input, option: any) =>
                        (option?.label?.toLowerCase() ?? '').includes(
                           input.toLowerCase()
                        )
                     }
                  />
               </div>
               <div style={{ marginRight: '10px', width: '100%' }}>
                  <div className={styles.title}>Select Leader</div>
                  <Select
                     showSearch
                     className={styles.select}
                     placeholder="Select the leader"
                     options={
                        leadersData?.map((leader: any) => ({
                           value: leader.id,
                           label: leader.username,
                        })) ?? []
                     }
                     onChange={(value) => {
                        console.log('value', value)
                        setLeader(value)
                     }}
                     value={leader}
                     style={{
                        marginTop: '10px',
                        width: '100%',
                     }}
                     filterOption={(input, option: any) =>
                        (option?.label?.toLowerCase() ?? '').includes(
                           input.toLowerCase()
                        )
                     }
                  />
               </div>
               <div style={{ marginRight: '10px', width: '100%' }}>
                  <div className={styles.title}>Select Driver</div>
                  <Select
                     showSearch
                     className={styles.select}
                     placeholder="Select the driver"
                     options={
                        driversData?.map((driver: any) => ({
                           value: driver.id,
                           label: driver.driver_name,
                        })) ?? []
                     }
                     onChange={(value) => {
                        console.log('value', value)
                        setDriver(value)
                     }}
                     value={driver}
                     style={{
                        marginTop: '10px',
                        width: '100%',
                     }}
                     filterOption={(input, option: any) =>
                        (option?.label?.toLowerCase() ?? '').includes(
                           input.toLowerCase()
                        )
                     }
                  />
               </div>
            </div>

            <div
               style={{
                  marginTop: '10px',
                  width: '100%',
                  display: selectType === 'flight' ? 'block' : 'none',
               }}
            >
               <div style={{ marginRight: '10px', width: '100%' }}>
                  <div className={styles.title}>Add Visa</div>
                  <Input
                     className={styles.input}
                     placeholder="Enter Visa"
                     type="text"
                     style={{ marginTop: '10px', width: '100%' }}
                     onChange={(e) => {
                        setVisa(e.target.value)
                     }}
                     value={visa}
                  />
               </div>
            </div>

            <div
               style={{
                  marginTop: '10px',
                  width: '100%',
               }}
            >
               <div style={{ marginRight: '10px', width: '100%' }}>
                  <div className={styles.title}>Add Note</div>
                  <Input
                     className={styles.input}
                     placeholder="Enter note"
                     type="text"
                     style={{ marginTop: '10px', width: '100%' }}
                     onChange={(e) => {
                        setNote(e.target.value)
                     }}
                     value={note}
                  />
               </div>
            </div>
         </div>

         <div className={styles.btnHolder}>
            <div className={styles.lineBreak} />
            <button className={styles.btnConfirm} onClick={handelConfirm}>
               Confirm
            </button>
            <button className={styles.btnCancel} onClick={() => onCancel?.()}>
               Cancel
            </button>
         </div>
      </div>
   )
}
